/**
 * salonBootstrap.js
 *
 * Resolves the active salon id at boot.
 *
 * In production the vitrina-gae edge resolves domain + slug (via `vitrina_key`)
 * and injects the id into `<meta name="salon-id" content="...">` before serving
 * index.html. Locally there is no edge in front, so fall back to a localStorage
 * override that lets a dev pin a salon by hand.
 *
 * Resolution order:
 *   1. `<meta name="salon-id">` content — edge-injected (prod / dev edge).
 *   2. localStorage `localhost-salon-id` — localhost only.
 *   3. null, plus a console hint with the exact command to set it.
 */
import { isLocalDevelopment } from '@/js/utils/environment.js';

const META_NAME = 'salon-id';
const LOCAL_KEY = 'localhost-salon-id';
// Older local setups stored the override with underscores — accept it too so
// an existing dev environment keeps working without re-setting the key.
const LEGACY_LOCAL_KEY = 'localhost_salon_id';

/**
 * @returns {string|null} the resolved salon id, or null if unresolved.
 */
export function resolveSalonId() {
  const meta = document.querySelector(`meta[name="${META_NAME}"]`);
  const injected = (meta?.getAttribute('content') || '').trim();
  if (injected) return injected;

  if (isLocalDevelopment()) {
    const local = (
      localStorage.getItem(LOCAL_KEY) ||
      localStorage.getItem(LEGACY_LOCAL_KEY) ||
      ''
    ).trim();
    if (local) {
      console.info(
        `[vitrina] localhost: usando salon-id de localStorage = ${local}`
      );
      return local;
    }
    console.warn(
      `[vitrina] localhost sin salon-id local asignado. Asígnalo con:\n` +
        `  localStorage.setItem('${LOCAL_KEY}', 'TU_SALON_SIMPLE_ID'); location.reload()`
    );
  }
  return null;
}

export { LOCAL_KEY as LOCALHOST_SALON_ID_KEY };
